import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface AssetPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (nameInput: string, file: File) => Promise<void>;
  isUploading?: boolean;
  error?: string | null;
}

export function AssetPickerDialog({
  open,
  onOpenChange,
  onUpload,
  isUploading = false,
  error = null,
}: AssetPickerDialogProps) {
  const [nameInput, setNameInput] = useState('');
  const [fileInput, setFileInput] = useState<File | null>(null);

  const handleUpload = async () => {
    if (!fileInput) return;
    await onUpload(nameInput.trim() || '', fileInput);
    // Reset form after successful upload
    setNameInput('');
    setFileInput(null);
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form when closing
      setNameInput('');
      setFileInput(null);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Asset</DialogTitle>
          <DialogDescription>
            Add a new asset to this tenant. Provide a display name and select a file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="asset-name" className="text-sm font-medium">
              Display Name (optional)
            </label>
            <Input
              id="asset-name"
              placeholder="Give your asset a friendly name"
              value={nameInput}
              onChange={(event) => setNameInput(event.target.value)}
              disabled={isUploading}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="asset-file" className="text-sm font-medium">
              Select File
            </label>
            <Input
              id="asset-file"
              type="file"
              onChange={(event) => setFileInput(event.target.files?.[0] ?? null)}
              disabled={isUploading}
            />
          </div>

          {fileInput && (
            <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
              <strong>Selected file:</strong> {fileInput.name} ({(fileInput.size / 1024 / 1024).toFixed(2)} MB)
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Images (PNG, JPG, GIF, WebP, SVG), videos (MP4, WebM), and other files are supported.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!fileInput || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

